(() => {
  'use strict';
  if (window.QuadroCommunitySlideshow) {
    window.QuadroCommunitySlideshow.init();
    return;
  }

  const controllers = new Map();
  const selector = '[data-community-slider]';

  function createSlider(slider) {
    const slides = [...slider.querySelectorAll('[data-slide]')];
    if (!slides.length) return null;
    const dots = [...slider.querySelectorAll('[data-dot]')];
    const track = slider.querySelector('.community-slider__track');
    const toggle = slider.querySelector('[data-toggle]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const autorotate = slides.length > 1 && slider.dataset.autorotate === 'true';
    const speed = Math.max(1000, Number(slider.dataset.speed) || 5000);
    const events = new AbortController();
    const signal = events.signal;
    const loads = new Map();
    const failedLoads = new Set();
    const loadCleanup = new Set();
    let current = 0, intended = 0, request = 0, timer = null;
    let paused = motion.matches, hovered = false, visible = false;
    let activated = false, ready = false, pending = false, destroyed = false;
    let pendingIntent = null;
    let drag = null, pointerToggleState = null;
    const relativeSteps = [];
    let stepFrame = null;

    const index = value => (value % slides.length + slides.length) % slides.length;
    const stop = () => { clearTimeout(timer); timer = null; };
    const listen = (node, type, handler, options = {}) => node?.addEventListener(type, handler, {...options, signal});
    function syncPlayback() {
      track?.setAttribute('aria-live', autorotate && !paused ? 'off' : 'polite');
      if (!toggle) return;
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      const pauseIcon = toggle.querySelector('[data-icon-pause]');
      const playIcon = toggle.querySelector('[data-icon-play]');
      if (pauseIcon) pauseIcon.hidden = paused;
      if (playIcon) playIcon.hidden = !paused;
    }
    function schedule() {
      stop();
      const mayRotate = autorotate && visible && !document.hidden && !paused && !hovered && !drag && !relativeSteps.length && stepFrame === null;
      if (pendingIntent?.automatic && !mayRotate) {
        request++;
        pendingIntent = null;
        pending = false;
        intended = current;
      }
      if (pendingIntent?.decoded && visible && !document.hidden) {
        // A manual choice made before leaving the viewport is applied only on return.
        const {target, relative} = pendingIntent;
        pendingIntent = null;
        pending = false;
        go(target, false, relative);
        return;
      }
      if (!destroyed && ready && visible && !document.hidden) neighbours(current);
      if (!destroyed && !pending && stepFrame === null && relativeSteps.length && visible && !document.hidden) {
        go(current + relativeSteps.shift(), false, true);
        return;
      }
      if (!destroyed && ready && mayRotate && !pending) {
        timer = setTimeout(() => go(current + 1, true), speed);
      }
    }
    function clearSteps() {
      relativeSteps.length = 0;
      if (stepFrame !== null) cancelAnimationFrame(stepFrame);
      stepFrame = null;
    }
    function step(direction) {
      activate();
      relativeSteps.push(direction);
      schedule();
    }
    function continueAfterPaint() {
      // Two animation frames leave a painted slide/dot state between queued steps.
      stepFrame = requestAnimationFrame(() => {
        stepFrame = requestAnimationFrame(() => {
          stepFrame = null;
          schedule();
        });
      });
    }

    // This controller owns these pictures. eu-core deliberately skips their deferred URLs.
    function load(position, retry = false, verify = false) {
      position = index(position);
      const retryFailed = retry && failedLoads.has(position);
      if (retryFailed) {
        loads.delete(position);
        failedLoads.delete(position);
      }
      const image = slides[position].querySelector('img[data-src], img');
      if (!image) return Promise.resolve(false);
      if (loads.has(position)) {
        const previous = loads.get(position);
        // <picture> may have selected another responsive source since prefetching.
        return verify ? previous.then(success => success ? decoded(image, position) : false) : previous;
      }
      const promise = decoded(image, position, () => {
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = position === intended ? 'auto' : 'low';
        image.closest('picture')?.querySelectorAll('source[data-srcset]').forEach(source => {
          source.srcset = source.dataset.srcset;
          source.removeAttribute('data-srcset');
        });
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        } else if (retryFailed) image.src = image.getAttribute('src');
      });
      loads.set(position, promise);
      return promise;
    }
    function decoded(image, position, prepare) {
      return new Promise(resolve => {
        let settled = false, decoding = false;
        const finish = result => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          loadCleanup.delete(cancel);
          if (!result && !destroyed) failedLoads.add(position);
          resolve(result && !destroyed);
        };
        const failed = () => finish(false);
        const cancel = () => finish(false);
        const loaded = async () => {
          if (decoding || settled || !image.complete) return;
          if (!image.naturalWidth) { finish(false); return; }
          const source = image.currentSrc;
          if (!source || source.startsWith('data:')) return;
          decoding = true;
          try {
            if (image.decode) await image.decode();
            decoding = false;
            if (source === image.currentSrc && image.complete && image.naturalWidth > 0) finish(true);
            else loaded();
          } catch (_) {
            decoding = false;
            if (!image.complete) return; // A responsive source changed during decode.
            if (source !== image.currentSrc) loaded();
            else finish(false);
          }
        };
        const timeout = setTimeout(failed, 15000);
        loadCleanup.add(cancel);
        image.addEventListener('load', loaded);
        image.addEventListener('error', failed);
        prepare?.();
        // A microtask lets src/source selection invalidate the transparent placeholder.
        queueMicrotask(loaded);
      });
    }
    function neighbours(position) {
      if (slides.length < 2) return;
      load(position - 1);
      load(position + 1);
    }
    function render(position) {
      current = position;
      slider.dataset.currentSlide = String(position);
      slides.forEach((slide, i) => {
        const active = i === position;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === position);
        dot.setAttribute('aria-current', String(i === position));
      });
    }
    function activate() {
      if (activated || destroyed) return;
      activated = true;
      load(current).then(success => {
        if (destroyed) return;
        ready = success;
        if (success) slider.dataset.mediaReady = 'true';
        schedule();
      });
      neighbours(current);
    }
    async function go(position, automatic = false, relative = false) {
      if (destroyed) return;
      activate();
      intended = index(position);
      const target = intended, token = ++request;
      pendingIntent = {target, automatic, relative, decoded:false};
      pending = true;
      stop();
      const success = await load(target, true, true);
      if (destroyed || token !== request) return;
      if (success && (!visible || document.hidden)) {
        if (automatic) {
          pendingIntent = null;
          pending = false;
          intended = current;
        } else pendingIntent.decoded = true;
        return;
      }
      pendingIntent = null;
      pending = false;
      if (success) {
        ready = true;
        render(target);
        if (relative) continueAfterPaint();
      } else {
        intended = current;
        // A failed neighbour stays on the decoded slide; do not repeat failed steps.
        if (relative) clearSteps();
      }
      schedule();
    }

    listen(slider.querySelector('[data-prev]'), 'click', () => step(-1));
    listen(slider.querySelector('[data-next]'), 'click', () => step(1));
    dots.forEach(dot => listen(dot, 'click', () => {
      clearSteps();
      go(Number(dot.dataset.index));
    }));
    listen(toggle, 'pointerdown', () => { pointerToggleState = !paused; });
    listen(toggle, 'pointercancel', () => { pointerToggleState = null; });
    listen(toggle, 'click', event => {
      paused = event.detail > 0 && pointerToggleState !== null ? pointerToggleState : !paused;
      pointerToggleState = null;
      syncPlayback();
      schedule();
    });
    listen(slider, 'focusin', () => { paused = true; syncPlayback(); schedule(); });
    listen(slider, 'mouseenter', () => { hovered = true; schedule(); });
    listen(slider, 'mouseleave', () => { hovered = false; schedule(); });
    listen(slider, 'dragstart', event => event.preventDefault());
    listen(slider, 'pointerdown', event => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || event.target.closest('button, a, input')) return;
      drag = {id:event.pointerId, x:event.clientX, y:event.clientY, delta:0, horizontal:false};
      slider.setPointerCapture?.(event.pointerId);
      schedule();
    });
    listen(slider, 'pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      drag.delta = event.clientX - drag.x;
      if (Math.abs(drag.delta) > 6 && Math.abs(drag.delta) > Math.abs(event.clientY - drag.y)) {
        drag.horizontal = true;
        slider.classList.add('is-dragging');
      }
    });
    const endDrag = event => {
      if (!drag || event.pointerId !== drag.id) return;
      const previous = drag;
      drag = null;
      slider.classList.remove('is-dragging');
      if (slider.hasPointerCapture?.(event.pointerId)) slider.releasePointerCapture(event.pointerId);
      if (event.type !== 'pointercancel' && previous.horizontal && Math.abs(previous.delta) >= 40) {
        step(previous.delta < 0 ? 1 : -1);
      } else schedule();
    };
    listen(slider, 'pointerup', endDrag);
    listen(slider, 'pointercancel', endDrag);
    listen(slider, 'lostpointercapture', endDrag);
    listen(document, 'visibilitychange', schedule);
    const motionChanged = () => { if (motion.matches) paused = true; syncPlayback(); schedule(); };
    if (motion.addEventListener) listen(motion, 'change', motionChanged);
    else motion.addListener(motionChanged);

    let nearObserver, viewObserver;
    if ('IntersectionObserver' in window) {
      nearObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          activate();
          nearObserver.disconnect();
        }
      }, {rootMargin:'300px 0px'});
      viewObserver = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        schedule();
      });
      nearObserver.observe(slider);
      viewObserver.observe(slider);
    } else {
      const checkViewport = () => {
        const rect = slider.getBoundingClientRect();
        visible = rect.bottom > 0 && rect.top < innerHeight;
        if (rect.bottom > -300 && rect.top < innerHeight + 300) activate();
        schedule();
      };
      listen(window, 'scroll', checkViewport, {passive:true});
      listen(window, 'resize', checkViewport);
      checkViewport();
    }
    render(0);
    syncPlayback();
    slider.dataset.initialized = 'true';
    return () => {
      destroyed = true;
      request++;
      stop();
      clearSteps();
      events.abort();
      nearObserver?.disconnect();
      viewObserver?.disconnect();
      if (!motion.addEventListener) motion.removeListener(motionChanged);
      [...loadCleanup].forEach(cancel => cancel());
      slider.classList.remove('is-dragging');
      delete slider.dataset.initialized;
      delete slider.dataset.mediaReady;
      delete slider.dataset.currentSlide;
    };
  }

  function init(root = document) {
    const sliders = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) sliders.unshift(root);
    sliders.forEach(slider => {
      if (controllers.has(slider)) return;
      const destroy = createSlider(slider);
      if (destroy) controllers.set(slider, destroy);
    });
  }
  function destroy(root) {
    controllers.forEach((cleanup, slider) => {
      if (root === slider || root.contains(slider)) {
        cleanup();
        controllers.delete(slider);
      }
    });
  }
  window.QuadroCommunitySlideshow = {init, destroy};
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();
